/**
 * Past due by age — `mockups/studio/money.html` §Failed payments' right-hand card.
 *
 * `StudioMoneyView['ageing']` is the data, one entry per `PAST_DUE_BUCKETS` band, in
 * order, already converted to one currency by `readmodels/studio-money.ts`. Nothing
 * here adds two amounts together: the columns are the bands as they were given, and
 * the sentence above them is a **predicate** over the bands (`isZero`, which is
 * `packages/shared/src/money.ts`'s own) rather than a figure this file worked out.
 *
 * **Two ticks, and both of them are real amounts.** `ColumnChart` wants an axis, and
 * the obvious axis — nice round numbers at $0 / $800 / $1,600, as the mockup draws —
 * needs `Math.ceil` on an amount, which is lint-banned outside `money.ts` and would
 * be inventing a figure besides. So the baseline is zero and the top tick is the
 * largest band's own amount: exact, honest, and the column labels in the table view
 * carry the rest. The mockup's own gridlines are the one thing on this card that is
 * not reproduced, and the report says so.
 *
 * `conclusion` and `table` are `Chart`'s required props for the reason its header
 * gives; both are derived here so that a page cannot pass a sentence that disagrees
 * with the columns beside it.
 */

import { isZero, zero as zeroMoney, type Money, type StudioMoneyView } from '@fareast/shared'

import { useFormat } from '../../format/useFormat.js'
import { Chart, ColumnChart, COLUMN_VIEW_BOX } from '../portal/Chart.js'
import { Stack } from '../portal/Layout.js'
import { Answer } from '../portal/Text.js'

type AgeingBand = StudioMoneyView['ageing'][number]

export type AgeingChartProps = {
  readonly bands: readonly AgeingBand[]
  /**
   * Slug → the company, for the invoices behind each band.
   *
   * `AgeingBandSchema.refs` is `"tunclaw FES-2026-046"` — the read model builds it
   * out of the **slug** and the reference — and the mockup prints "TuncLaw $520".
   * Every other table on the money page runs a slug through this, and a chart is not
   * the place to start saying `vela-interiors` at people. Left out, the refs print as
   * they arrived.
   */
  readonly nameOf?: ((client: string) => string) | undefined
}

export function AgeingChart({ bands, nameOf }: AgeingChartProps) {
  const format = useFormat()

  const largest = largestOf(bands)
  const conclusion = conclusionOf(bands)
  const named = (refs: readonly string[]) => refs.map((ref) => spellRef(ref, nameOf)).join(', ')

  return (
    <Stack gap="tight">
      {/*
        The sentence, said out loud as well as labelled.

        `mockups/studio/money.html` opens this card with `<p class="answer">Nothing has
        ever aged past 60 days.</p>` and *then* draws the chart — the conclusion is the
        thing a reader is meant to take away, and an `aria-label` is not read by anybody
        looking at the screen. CONTRACT.md §2.13 still requires the label to state the
        conclusion, so it is the same sentence twice: once for a reader and once for the
        chart's accessible name. Saying it only once, in the label, was the version of
        this that printed a bare chart to everybody who can see.
      */}
      <Answer>{conclusion}</Answer>

      <Chart
        conclusion={conclusion}
        viewBox={COLUMN_VIEW_BOX}
        table={{
          columns: [
            { label: 'Age' },
            { label: 'Past due', right: true },
            { label: 'Which invoices' },
          ],
          rows: bands.map((band) => [
            band.bucket,
            format.money(band.amount),
            band.refs.length === 0 ? 'None' : named(band.refs),
          ]),
        }}
      >
        <ColumnChart
          data={bands.map((band) => ({
            label: band.bucket,
            value: band.amount.amount,
            title: `${band.bucket}: ${format.money(band.amount)}${
              band.refs.length === 0 ? '' : ` — ${named(band.refs)}`
            }`,
          }))}
          ticks={ticksFor(largest, format.money)}
        />
      </Chart>
    </Stack>
  )
}

/**
 * `"tunclaw FES-2026-046"` → `"TuncLaw FES-2026-046"`.
 *
 * The read model joins a slug and a reference with a space, so the slug is everything
 * before the first one. A `refs` entry that is not that shape — a bare reference — is
 * left exactly as it came, because guessing at it would be worse than printing it.
 */
export function spellRef(ref: string, nameOf?: (client: string) => string): string {
  if (nameOf === undefined) return ref
  const space = ref.indexOf(' ')
  if (space <= 0) return ref
  return `${nameOf(ref.slice(0, space))}${ref.slice(space)}`
}

/**
 * The sentence the chart is announced with — `mockups/studio/money.html`'s "Nothing
 * has ever aged past 60 days."
 *
 * Derived from the bands rather than passed in, and it says which band is the last
 * one with money in it rather than translating a bucket label into "60 days": the
 * buckets are `PAST_DUE_BUCKETS`' own strings and parsing one into a number here
 * would be this file having an opinion about a list it does not own.
 */
export function conclusionOf(bands: readonly AgeingBand[]): string {
  const withMoney = bands.filter((band) => !isZero(band.amount))
  const oldest = withMoney[withMoney.length - 1]

  if (oldest === undefined) return 'Nothing is past due.'
  if (oldest === bands[bands.length - 1]) {
    return `Money past due reaches the oldest band there is, ${oldest.bucket}.`
  }
  return `Nothing past due has aged beyond the ${oldest.bucket} band.`
}

/**
 * The axis: zero, and the largest band's own amount above it.
 *
 * A month where nothing is past due gets **one** tick, not two at the same height —
 * `isBaseline` picks the lowest by value, and two zeroes would draw the heavy
 * `.axisline` twice over the same pixels with the amount printed on both.
 */
function ticksFor(
  largest: AgeingBand | undefined,
  money: (value: Money) => string,
): readonly { value: number; label: string }[] {
  if (largest === undefined) return []
  const zero = { value: 0, label: money(zeroMoney(largest.amount.currency)) }
  if (largest.amount.amount === 0) return [zero]
  return [{ value: largest.amount.amount, label: money(largest.amount) }, zero]
}

/** The band the axis is scaled to. Undefined only when there are no bands at all. */
function largestOf(bands: readonly AgeingBand[]): AgeingBand | undefined {
  let found: AgeingBand | undefined
  for (const band of bands) {
    if (found === undefined || band.amount.amount > found.amount.amount) found = band
  }
  return found
}
